import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormNumber,
  FormRoot,
  Label,
  zodResolver,
} from "@devhop/ui";
import { useEffect } from "react";
import * as RHF from "react-hook-form";
import { z } from "zod";
import type {
  CreateLeaveEntitlementDTO,
  LeaveTypeListItem,
} from "@/modules/hr/leave/domain/types";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

const LeaveEntitlementAddFormSchema = z.object({
  leaveTypeId: z.string().min(1, "ກະລຸນາເລືອກປະເພດລາ"),
  totalDays: z.number().min(0, "ວັນທີ່ມີສິດຕ້ອງບໍ່ຕໍ່າກວ່າ 0"),
  usedDays: z.number().min(0, "ວັນທີ່ໃຊ້ແລ້ວຕ້ອງບໍ່ຕໍ່າກວ່າ 0"),
  carriedOverDays: z.number().min(0, "ວັນເກັບເຂົ້າມາຕ້ອງບໍ່ຕໍ່າກວ່າ 0"),
});

export type LeaveEntitlementAddFormValues = z.infer<
  typeof LeaveEntitlementAddFormSchema
>;

type LeaveEntitlementAddTypeDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employeeId: string;
  year: number;
  employeeDisplayName: string;
  missingLeaveTypes: LeaveTypeListItem[];
  onSubmit: (dto: CreateLeaveEntitlementDTO) => void;
  isSubmitting?: boolean;
};

export function LeaveEntitlementAddTypeDialog({
  open,
  onOpenChange,
  employeeId,
  year,
  employeeDisplayName,
  missingLeaveTypes,
  onSubmit,
  isSubmitting,
}: LeaveEntitlementAddTypeDialogProps) {
  const methods = RHF.useForm<LeaveEntitlementAddFormValues>({
    // biome-ignore lint/suspicious/noExplicitAny: zodResolver type inference
    resolver: zodResolver(LeaveEntitlementAddFormSchema) as any,
    defaultValues: {
      leaveTypeId: "",
      totalDays: 0,
      usedDays: 0,
      carriedOverDays: 0,
    },
  });

  useEffect(() => {
    if (!open || missingLeaveTypes.length === 0) return;
    const firstId = missingLeaveTypes[0]?.id ?? "";
    methods.reset({
      leaveTypeId: firstId,
      totalDays: 0,
      usedDays: 0,
      carriedOverDays: 0,
    });
  }, [open, methods, missingLeaveTypes]);

  const selectOptions = missingLeaveTypes.map((t) => ({
    value: t.id,
    label: t.code ? `${t.name} (${t.code})` : t.name,
  }));

  const handleValid = (vals: LeaveEntitlementAddFormValues) => {
    onSubmit({
      employeeId,
      leaveTypeId: vals.leaveTypeId,
      year,
      totalDays: vals.totalDays,
      usedDays: vals.usedDays,
      carriedOverDays: vals.carriedOverDays,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>ເພີ່ມປະເພດສິດລາ</DialogTitle>
        </DialogHeader>
        <div className="mb-4 text-muted-foreground text-sm">
          {employeeDisplayName} — ປີ {year}
        </div>
        <FormRoot<LeaveEntitlementAddFormValues>
          methods={methods}
          onSubmit={handleValid}
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label htmlFor="add-entitlement-leave-type">ປະເພດລາ</Label>
            <RHF.Controller
              name="leaveTypeId"
              control={methods.control}
              render={({ field }) => (
                <SimpleSelect
                  id="add-entitlement-leave-type"
                  options={selectOptions}
                  value={field.value}
                  onValueChange={field.onChange}
                  placeholder="ເລືອກປະເພດລາ"
                  className="w-full"
                  disabled={
                    missingLeaveTypes.length === 0 || isSubmitting === true
                  }
                />
              )}
            />
            {methods.formState.errors.leaveTypeId?.message ? (
              <p className="text-destructive text-sm">
                {methods.formState.errors.leaveTypeId.message}
              </p>
            ) : null}
          </div>
          <FormNumber name="totalDays" label="ວັນທີ່ມີສິດ" min={0} />
          <FormNumber name="usedDays" label="ໃຊ້ແລ້ວ" min={0} />
          <FormNumber name="carriedOverDays" label="ເກັບເຂົ້າມາ" min={0} />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              ຍົກເລີກ
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || missingLeaveTypes.length === 0}
              isLoading={isSubmitting}
            >
              ບັນທຶກ
            </Button>
          </DialogFooter>
        </FormRoot>
      </DialogContent>
    </Dialog>
  );
}
